import { ParsedCsvRow, AnalysisParams } from '@/components/user/csv-analysis/types';

export interface SequenceAnalysisResult {
  sequence: string[];
  sequenceString: string;
  totalProfit: number;
  maxDrawdown: number;
  profitDDRatio: number;
  winRate: number;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  startAfter: number;
  stopAfter: number;
  status: 'optimal' | 'good' | 'poor';
}

export class PositionSequenceOptimizer {
  /**
   * Generate all possible consecutive position size sequences from available positions
   */
  static generatePositionSequences(positionSizes: string[]): string[][] {
    const sequences: string[][] = [];
    
    // Generate sequences of length 2 to positionSizes.length
    for (let length = 2; length <= positionSizes.length; length++) {
      for (let start = 0; start <= positionSizes.length - length; start++) {
        const sequence = positionSizes.slice(start, start + length);
        sequences.push(sequence);
      }
    }
    
    return sequences;
  }

  /**
   * Analyze a single position sequence by filtering trades and calculating metrics
   */
  static analyzeSequence(
    csvData: ParsedCsvRow[],
    sequence: string[],
    params: AnalysisParams,
    sortedPositions: string[]
  ): SequenceAnalysisResult {
    // Filter data by position sizes in sequence
    const filteredData = csvData.filter(trade => 
      sequence.includes(trade.position_size_raw)
    );

    if (filteredData.length === 0) {
      return this.createEmptyResult(sequence, sortedPositions);
    }

    // Sort by timestamp
    const sortedData = [...filteredData].sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateA - dateB;
    });

    // Calculate statistics using normalized PnL (same as Run Analysis)
    const activeTrades = sortedData;
    
    // Normalize P&L based on TP/SL and quantity (same logic as CsvAnalysisPanel)
    const normalizedTrades = activeTrades.map(trade => {
      let normalizedPnl = trade.pnl;
      
      if (params.tp_usd > 0 || params.sl_usd > 0) {
        const pq = params.quantity_base > 0 ? (trade.position_size / params.quantity_base) : 1;
        
        if (trade.pnl > 0 && params.tp_usd > 0) {
          normalizedPnl = params.tp_usd * pq;
        } else if (trade.pnl < 0 && params.sl_usd > 0) {
          normalizedPnl = -params.sl_usd * pq;
        }
      }
      
      return { ...trade, normalized_pnl: normalizedPnl };
    });
    
    const winningTrades = normalizedTrades.filter(trade => trade.normalized_pnl > 0);
    const losingTrades = normalizedTrades.filter(trade => trade.normalized_pnl < 0);
    
    const totalProfit = normalizedTrades.reduce((sum, trade) => sum + trade.normalized_pnl, 0);
    const winRate = normalizedTrades.length > 0 ? (winningTrades.length / normalizedTrades.length) * 100 : 0;

    // Calculate max drawdown using normalized PnL
    let maxDrawdown = 0;
    let peak = params.initial_capital || 0;
    let runningPnl = 0;
    
    for (const trade of normalizedTrades) {
      runningPnl += trade.normalized_pnl;
      const currentEquity = (params.initial_capital || 0) + runningPnl;
      peak = Math.max(peak, currentEquity);
      const drawdown = peak - currentEquity;
      maxDrawdown = Math.max(maxDrawdown, drawdown);
    }

    const profitDDRatio = maxDrawdown > 0 ? totalProfit / maxDrawdown : totalProfit > 0 ? Infinity : 0;
    
    // Calculate Start After and Stop After indices
    const startAfter = sortedPositions.indexOf(sequence[0]);
    const stopAfter = sortedPositions.indexOf(sequence[sequence.length - 1]) + 1;
    
    // Determine status based on criteria
    let status: 'optimal' | 'good' | 'poor' = 'poor';
    if (profitDDRatio >= 3.0 && winRate <= 70) {
      status = 'optimal';
    } else if (profitDDRatio >= 2.0 || winRate <= 75) {
      status = 'good';
    }

    return {
      sequence,
      sequenceString: sequence.join(' → '),
      totalProfit,
      maxDrawdown,
      profitDDRatio,
      winRate,
      totalTrades: normalizedTrades.length,
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      startAfter,
      stopAfter,
      status,
    };
  }

  /**
   * Find optimal position sequences from CSV data
   */
  static findOptimalSequences(
    csvData: ParsedCsvRow[],
    availablePositions: string[],
    params: AnalysisParams
  ): SequenceAnalysisResult[] {
    // Sort positions from smallest to largest for consistent indexing
    const sortedPositions = [...availablePositions].sort((a, b) => parseFloat(a) - parseFloat(b));
    
    // Generate all possible sequences
    const sequences = this.generatePositionSequences(sortedPositions);
    
    // Analyze each sequence
    const results = sequences.map(sequence => 
      this.analyzeSequence(csvData, sequence, params, sortedPositions)
    );

    // Sort by profit/DD ratio descending, then by win rate ascending
    return results.sort((a, b) => {
      if (Math.abs(a.profitDDRatio - b.profitDDRatio) > 0.1) {
        return b.profitDDRatio - a.profitDDRatio;
      }
      return a.winRate - b.winRate;
    });
  }

  /**
   * Create empty result for sequences with no data
   */
  private static createEmptyResult(sequence: string[], sortedPositions: string[]): SequenceAnalysisResult {
    const startAfter = sortedPositions.indexOf(sequence[0]);
    const stopAfter = sortedPositions.indexOf(sequence[sequence.length - 1]) + 1;
    
    return {
      sequence,
      sequenceString: sequence.join(' → '),
      totalProfit: 0,
      maxDrawdown: 0,
      profitDDRatio: 0,
      winRate: 0,
      totalTrades: 0,
      winningTrades: 0,
      losingTrades: 0,
      startAfter,
      stopAfter,
      status: 'poor',
    };
  }
}